import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LoggingService, PurchaseService, SignalRUnauthrosiedHub, AuthService } from '@fgb/core';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// We can use this in the future to track different navigation origins and handle them accordingly
export enum RegisterNavigationOrigin {
  External = 'external',
}

@Component({
  selector: 'fgbcl-registration-payment-callback-page',
  templateUrl: './registration-payment-callback-page.component.html',
  styleUrls: ['./registration-payment-callback-page.component.scss'],
})
export class RegistrationPaymentCallbackPageComponent implements OnInit, OnDestroy {
  private _destroyed$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private purchaseService: PurchaseService,
    private logService: LoggingService,
    private signalRHub: SignalRUnauthrosiedHub,
    private router: Router,
    private authService: AuthService
  ) {}

  ngOnInit() {
    let orderId: string | null = null;
    if (this.route.snapshot.queryParamMap.keys.map((x) => x.toUpperCase()).includes('ORDERID')) {
      const orderIdQueryStringKey = this.route.snapshot.queryParamMap.keys.filter((x) => x.toUpperCase() === 'ORDERID')[0];
      orderId = this.route.snapshot.queryParamMap.get(orderIdQueryStringKey);
    }

    const origin = this.route.snapshot.queryParamMap.get('origin') as RegisterNavigationOrigin;

    const params = this.cleanQueryParams({
      orderId,
      origin,
    });

    if (!orderId) {
      this.logService.error('No order ID could be found in the query string');
      return;
    }

    if (this.authService.isAuthenticated()) {
      this.purchaseService
        .completeTransaction(true)
        .toPromise()
        .then(() => this.refreshUserContext())
        .then(() => {
          this.router.navigate(['/register/success'], { queryParams: params });
        })
        .catch(() => {
          this.router.navigate(['/register/failure']);
        });
    } else {
      this.signalRHub.connectByOrderId(orderId);
    }

    this.signalRHub.connectionEstablished$.pipe(takeUntil(this._destroyed$)).subscribe((connection) => {
      this.logService.debug('SignalR connection', connection);
    });
  }

  /**
   * Completing the transaction finishes the registration, so the account is no longer a first time
   * user on the server. Pull a fresh access token to pick that up before landing on the success
   * page, otherwise the login button there routes through the first time user guard with a stale
   * account status and drops the user back into the registration wizard.
   * A failure here must not fail the payment, so the error is logged and the flow continues.
   */
  private refreshUserContext(): Promise<void> {
    return firstValueFrom(this.authService.refreshAccessTokenAndContext())
      .then(() => undefined)
      .catch((error) => {
        this.logService.error('Failed to refresh the auth token after completing the registration payment.', error);
      });
  }

  private cleanQueryParams(params: Record<string, any>) {
    return Object.fromEntries(
      Object.entries(params).filter(([_, value]) => value !== null && value !== undefined && value !== '')
    );
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }
}
